import { cn } from "@synthesis/shared-utils";

import { getApplicantTypeLabel, type ApplicantDataFormValues } from "../../../create-application-page/create-application-page.lib";
import { useApplicationDictionariesContext } from "../../application-dictionaries-context";
import type { ApplicationDetailField } from "../../application-detail-page.lib";
import { DetailSection } from "../detail-section";

type ApplicantPayerContentProps = {
  applicantData: ApplicantDataFormValues | null;
  className?: string;
};

type ApplicantPayerDetailSection = {
  title: string;
  fields: ApplicationDetailField[];
};

const formatApplicantValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Да" : "Нет";
  }

  return String(value);
};

const buildStringListFields = (label: string, values: string[] | undefined): ApplicationDetailField[] => {
  const filledValues = (values ?? []).map((value) => value.trim()).filter(Boolean);

  if (filledValues.length === 0) {
    return [{ label, value: "—" }];
  }

  return filledValues.map((value) => ({ label, value }));
};

const buildPayerContractEmailFields = (applicantData: ApplicantDataFormValues): ApplicationDetailField[] => {
  if (!applicantData.payerUseSeparateContractEmail) {
    return [];
  }

  return buildStringListFields("E-mail для направления договора", applicantData.payerContractEmails);
};

const buildPayerDetailSections = (applicantData: ApplicantDataFormValues, payerApplicantTypeLabel: string): ApplicantPayerDetailSection[] => {
  const isIndividualEntrepreneur = applicantData.payerApplicantType === "SOLE_PROPRIETOR";

  return [
    {
      title: "Регистрационные реквизиты плательщика",
      fields: [
        { label: "Тип заявителя / лица", value: payerApplicantTypeLabel },
        { label: "Полное наименование / ИП ФИО", value: formatApplicantValue(applicantData.payerOrganization) },
        ...(isIndividualEntrepreneur
          ? [
              { label: "ИНН", value: formatApplicantValue(applicantData.payerInn) },
              { label: "ОГРН / ОГРНИП", value: formatApplicantValue(applicantData.payerOgrn) },
            ]
          : [
              { label: "Сокращённое наименование", value: formatApplicantValue(applicantData.payerShortOrganizationName) },
              { label: "ИНН", value: formatApplicantValue(applicantData.payerInn) },
              { label: "ОГРН / ОГРНИП", value: formatApplicantValue(applicantData.payerOgrn) },
              { label: "ОКВЭД", value: formatApplicantValue(applicantData.payerOkved) },
              { label: "ОКТМО / ОКАТО", value: formatApplicantValue(applicantData.payerOktmoOkato) },
              { label: "ОКПО", value: formatApplicantValue(applicantData.payerOkpo) },
              { label: "КПП", value: formatApplicantValue(applicantData.payerKpp) },
            ]),
      ],
    },
    {
      title: "Контактная информация плательщика",
      fields: [
        { label: "Юридический адрес / Адрес регистрации (для ИП)", value: formatApplicantValue(applicantData.payerLegalAddress) },
        { label: "Фактический адрес", value: formatApplicantValue(applicantData.payerActualAddress) },
        { label: "Почтовый адрес", value: formatApplicantValue(applicantData.payerPostalAddress) },
        ...buildStringListFields("Контактный телефон организации", applicantData.payerPhones),
        ...buildStringListFields("E-mail организации", applicantData.payerEmails),
        ...buildPayerContractEmailFields(applicantData),
      ],
    },
    {
      title: "Контактное лицо по вопросам оплаты",
      fields: [
        { label: "Контактное лицо (ФИО)", value: formatApplicantValue(applicantData.payerPaymentContactFullName) },
        { label: "Должность", value: formatApplicantValue(applicantData.payerPaymentContactPosition) },
        ...buildStringListFields("Контактный телефон", applicantData.payerPaymentContactPhones),
        ...buildStringListFields("e-mail для связи", applicantData.payerPaymentContactEmails),
      ],
    },
    {
      title: "Банковские реквизиты плательщика",
      fields: [
        { label: "Банк", value: formatApplicantValue(applicantData.payerBankName) },
        { label: "БИК", value: formatApplicantValue(applicantData.payerBik) },
        { label: "Расчётный счёт", value: formatApplicantValue(applicantData.payerSettlementAccount) },
        { label: "Корреспондентский счёт", value: formatApplicantValue(applicantData.payerCorrespondentAccount) },
      ],
    },
  ];
};

export const ApplicantPayerContent = ({ applicantData, className }: ApplicantPayerContentProps) => {
  const { dictionaries } = useApplicationDictionariesContext();

  if (!applicantData?.paymentByAnotherOrganization) {
    return null;
  }

  const payerApplicantTypeLabel =
    dictionaries.applicantTypes.find((option) => option.value === applicantData.payerApplicantType)?.label ??
    getApplicantTypeLabel(applicantData.payerApplicantType);

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {buildPayerDetailSections(applicantData, payerApplicantTypeLabel).map((section) => (
        <DetailSection key={section.title} title={section.title} fields={section.fields} />
      ))}
    </div>
  );
};
